import { fromPromise } from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import type { OperationDefinitionNode } from 'graphql'
import type { Client } from 'graphql-ws'

import { router } from '@/router'
import { useUserStore } from '@/stores/user'
import { getRefreshToken } from '@/utils/cookies'

export const createErrorHandler = (wsClient?: Client) => {
  let isRedirecting = false
  let isRefreshing = false
  let pendingRequests: Array<() => void> = []

  const resolvePendingRequests = () => {
    pendingRequests.forEach(cb => cb())
    pendingRequests = []
  }

  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (import.meta.env.DEV) {
      if (graphQLErrors) console.log('GraphQL Errors:', graphQLErrors)
      if (networkError) console.log('GraphQL Network Error:', networkError)
    }

    let isUnauthenticated = graphQLErrors?.some(e =>
      ['JWT102', 'JWT100'].includes(e.extensions?.code as string),
    )

    const errorResult = (networkError as any)?.result
    if (errorResult) {
      isUnauthenticated ||= ['JWT102', 'JWT100'].includes(errorResult.extensions?.code)
    }

    if (isUnauthenticated && !isRedirecting) {
      isRedirecting = true
      useUserStore().resetAppToDefault()
      router.push('/login')
    }

    // token invalid handling
    let isTokenInvalid = graphQLErrors?.some(e => e.extensions?.code === 'JWT101')
    if (errorResult) {
      isTokenInvalid ||= ['JWT101'].includes(errorResult.extensions?.code)
    }

    const operationType = (operation.query.definitions[0] as OperationDefinitionNode).operation

    if ((networkError && networkError.message === 'invalid_token') || isTokenInvalid) {
      if (operationType === 'subscription' && wsClient) {
        wsClient.terminate()
      }

      let forward$
      if (!isRefreshing) {
        isRefreshing = true
        forward$ = fromPromise(
          useUserStore()
            .refreshTokenAction(getRefreshToken() || '')
            .then(accessToken => {
              resolvePendingRequests()
              return accessToken
            })
            .catch(() => {
              pendingRequests = []
              useUserStore().resetAppToDefault()
              router.push('/login')
            })
            .finally(() => {
              isRefreshing = false
            }),
        ).filter(Boolean)
      } else {
        forward$ = fromPromise(
          new Promise(resolve => {
            pendingRequests.push(() => resolve(true))
          }),
        )
      }

      return forward$.flatMap(() => {
        const oldHeaders = operation.getContext().headers
        const token = useUserStore().token
        operation.setContext({
          headers: {
            ...oldHeaders,
            Authorization: `Bearer ${token}`,
          },
        })
        return forward(operation)
      })
    }
  })
}

// Ensure HMR cleanup
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    console.log('Disposing errorHandler module...')
  })
}
